import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dailyRewardsAPI } from '../../../services/api';
import { fetchWalletBalance } from '../../wallet/store/walletSlice';
import i18n from '../../../i18n';

// Async thunk для получения информации о стрике
export const getStreakInfo = createAsyncThunk(
  'dailyRewards/getStreakInfo',
  async (_, { rejectWithValue }) => {
    console.log('🔄 [DailyRewardsSlice] getStreakInfo thunk started');
    try {
      const result = await dailyRewardsAPI.getStreakInfo();
      console.log('✅ [DailyRewardsSlice] getStreakInfo success:', result);
      return result;
    } catch (error) {
      console.error('❌ [DailyRewardsSlice] getStreakInfo error:', error);
      return rejectWithValue(error.response?.data?.message || i18n.t('daily.load_failed', 'Failed to get streak info'));
    }
  }
);

// Async thunk только для получения награды
export const claimDailyReward = createAsyncThunk(
  'dailyRewards/claimReward',
  async (_, { rejectWithValue, dispatch }) => {
    console.log('🔄 [DailyRewardsSlice] claimDailyReward thunk started');
    try {
      const result = await dailyRewardsAPI.claimReward();
      console.log('✅ [DailyRewardsSlice] claimDailyReward success:', result);
      // Refresh wallet balance so Balance header shows new coins/tickets immediately
      dispatch(fetchWalletBalance());
      return result;
    } catch (error) {
      // 404 "No unclaimed streak bonus for today" means already claimed — not an error
      if (error.response?.status === 404) {
        console.log('ℹ️ [DailyRewardsSlice] Already claimed today, treating as success');
        return { alreadyClaimed: true };
      }
      console.error('❌ [DailyRewardsSlice] claimDailyReward error:', error);
      return rejectWithValue(error.response?.data?.message || i18n.t('daily.claim_failed', 'Failed to claim reward'));
    }
  }
);

const initialState = {
  isLoading: false,
  isClaiming: false,
  error: null,
  currentStreak: 0,
  totalStreak: 0,
  lastLoginDate: null,
  rewards: {
    coinsReward: 0,
    ticketsReward: 0
  },
  streakConfig: [
    { day: 1, coins: 70, tickets: 1 },
    { day: 2, coins: 100, tickets: 1 },
    { day: 3, coins: 150, tickets: 1 },
    { day: 4, coins: 200, tickets: 2 },
    { day: 5, coins: 250, tickets: 2 },
    { day: 6, coins: 300, tickets: 2 },
    { day: 7, coins: 500, tickets: 3 }
  ]
};

const dailyRewardsSlice = createSlice({
  name: 'dailyRewards',
  initialState,
  reducers: {
    clearError: (state) => {
      console.log('🧹 [DailyRewardsSlice] Clearing error');
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get streak info
      .addCase(getStreakInfo.pending, (state) => {
        console.log('⏳ [DailyRewardsSlice] getStreakInfo.pending');
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getStreakInfo.fulfilled, (state, action) => {
        console.log('✅ [DailyRewardsSlice] getStreakInfo.fulfilled:', action.payload);
        state.isLoading = false;
        // Backend returns StreakBonusDTO: { currentStreak, coinsReward, ticketsReward }
        state.currentStreak = action.payload.currentStreak || 0;
        state.rewards = {
          coinsReward: action.payload.rewards?.coinsReward ?? action.payload.coinsReward ?? 0,
          ticketsReward: action.payload.rewards?.ticketsReward ?? action.payload.ticketsReward ?? 0,
        };
      })
      .addCase(getStreakInfo.rejected, (state, action) => {
        console.error('❌ [DailyRewardsSlice] getStreakInfo.rejected:', action.payload);
        state.isLoading = false;
        state.error = action.payload;
      })
      // Claim reward
      .addCase(claimDailyReward.pending, (state) => {
        console.log('⏳ [DailyRewardsSlice] claimDailyReward.pending');
        state.isClaiming = true;
        state.error = null;
      })
      .addCase(claimDailyReward.fulfilled, (state, action) => {
        console.log('✅ [DailyRewardsSlice] claimDailyReward.fulfilled:', action.payload);
        state.isClaiming = false;
        if (action.payload?.alreadyClaimed) return;
        // Обновляем стрик после получения награды
        state.currentStreak = action.payload.newStreak;
        state.rewards = {
          coinsReward: action.payload.coinsReward,
          ticketsReward: action.payload.ticketsReward
        };
        console.log('🎁 [DailyRewardsSlice] Reward claimed successfully');
      })
      .addCase(claimDailyReward.rejected, (state, action) => {
        console.error('❌ [DailyRewardsSlice] claimDailyReward.rejected:', action.payload);
        state.isClaiming = false;
        state.error = action.payload;
      });
  }
});

export const { clearError } = dailyRewardsSlice.actions;
export default dailyRewardsSlice.reducer;